import { useClubImportNudge } from "@/components/features/ClubImport/useClubImportNudge";
import { CreditCostMarker } from "@/components/features/Credits/CreditCostMarker";
import SBC from "@/components/organisms/SBCs/SBC/SBC";
import { Button } from "@/design-system/Button/Button";
import { trackSolveSbc } from "@/events/sbcMixpanel";
import { useEmptyClubGuard } from "@/features/MyClub/EmptyClub/useEmptyClubGuard";
import { CheapestCombinationCards } from "@/pages/Sbcs/SbcSetPage/components/CheapestCombinationCards";
import { showModal } from "@/redux/message/messageSlice";
import { sbcFiltersSelector } from "@/redux/sbcs/sbcFiltersSlice";
import {
  Sbc,
  getSbcSetSelector,
  getSbcsSelector,
  markSbcSolved,
  solveSbc,
} from "@/redux/sbcs/sbcsSlice";
import { AppDispatch } from "@/redux/store";
import {
  getUserSelector,
  solvesWithClubSelector,
} from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { useCreditGate } from "@repo/shared/redux";
import { sbcPath } from "@repo/shared/utils";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { NavLink } from "react-router";

type Props = {
  sbc: Sbc;
  isOnSolutionsViewPage?: boolean;
  disableComplete?: boolean;
};

const GetCardSBC: React.FC<Props> = ({
  sbc,
  isOnSolutionsViewPage,
  disableComplete,
}) => {
  const { t } = useTranslation("sbcs");
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { setId, name: setSlug } = useParams();
  const { sbcSetStatus } = useSelector(getSbcsSelector);
  const sbcSet = useSelector(getSbcSetSelector(+setId));
  const filters = useSelector(sbcFiltersSelector);
  const { user } = useSelector(getUserSelector);
  const solvesWithClub = useSelector(solvesWithClubSelector);
  const creditGate = useCreditGate("solve-sbc-club");
  const nudgeClubImport = useClubImportNudge("sbc-solver");
  const { shouldPromptEmptyClub, promptEmptyClub } = useEmptyClubGuard({
    section: "SBC",
    subsection: "Set View",
  });

  /*
    A Streamline challenge is solved one card at a time on its own screen, and
    always from the club — so the solve is the navigation rather than a thunk
    fired before it. A normal challenge draws on the club whenever there is
    one: the backend runs the club pools alongside the market.
  */
  const isStreamline = sbcSet?.kind === "STREAMLINE";
  const usesClub = isStreamline || solvesWithClub;
  const cheapestRows =
    user.platform === "PC"
      ? sbc.cheapestCombination?.pc
      : sbc.cheapestCombination?.ps;

  useEffect(() => {
    if (sbcSetStatus === "rejected") navigate("/");
    window.scrollTo(0, 0);
  }, [sbcSetStatus, navigate]);

  /* A Streamline challenge has no market-only mode to fall back on, so an
     empty club (or signed out, which counts as one) stops at the card rather
     than bouncing off the solution page. A normal challenge without a club
     just gets the market solve. The link itself stays intact for crawlers. */
  const solvesWithEmptyClub =
    isStreamline && (shouldPromptEmptyClub || !user.uuid);

  const handleSBCClicked = (e: React.MouseEvent) => {
    // Same as the empty-club branch below: the solve never went out, so the
    // navigation the card would otherwise perform has to be called off too —
    // it would land on the previous solution with the gate floating over it.
    if (!creditGate({ qualifying: usesClub, sbcId: sbc.id })) {
      e.preventDefault();
      return;
    }

    if (solvesWithEmptyClub) {
      e.preventDefault();
      promptEmptyClub();
      return;
    }

    if (!isStreamline) dispatch(solveSbc(sbc.id));
    // After the solve, not instead of it: the nudge rides on the wait.
    nudgeClubImport({ subsection: "Set View" });
    trackSolveSbc({
      subsection: "Set View",
      categoryId: sbcSet?.categoryId,
      filters,
      scope: usesClub ? "club" : "market",
    });
  };

  const setLink = sbcSet ?? { id: sbc.setId, name: "", slug: setSlug };
  const solvePath = sbcPath(setLink, sbc);

  return (
    <SBC
      disableComplete={disableComplete}
      {...sbc}
      key={sbc.name}
      /*
        The marker sits beside the button rather than inside it, and on its
        left: the group is right-anchored in the foot of the tile, so a marker
        that appears grows leftward and the button never moves. It follows the
        scope, since a market-only solve is free — the same predicate the gate
        uses below.
      */
      solveAction={
        /* The only target on the tile. The href is what keeps the solution
           pages crawlable, and the handler is what performs the solve. */
        <NavLink to={solvePath} onClick={handleSBCClicked} className="block">
          {/* A club solve costs a Credit, a market-only one is free. */}
          <Button
            variant={usesClub ? "credit-magic" : "magic"}
            size="medium"
            stretch
          >
            {/* The label is centred in the full-width button and stays put:
                the price hangs off its left edge out of flow, so a card that
                shows one does not shunt its label sideways. */}
            <span className="relative flex items-center">
              <CreditCostMarker
                featureId="solve-sbc-club"
                when={usesClub}
                placement="beside-button"
              />
              <span className={usesClub ? "text-holo" : undefined}>
                {t("sbcCard.buildSolutions")}
              </span>
            </span>
          </Button>
        </NavLink>
      }
      suggestedSolution={
        cheapestRows?.length ? (
          <CheapestCombinationCards rows={cheapestRows} />
        ) : undefined
      }
      leadWithSolution={isStreamline}
      imageURL={sbcSet?.sbcsCount < 2 ? sbcSet?.imageURL : sbc.imageURL}
      isOnSolutionsPage={isOnSolutionsViewPage}
      onComplete={(completed) => {
        if (user.uuid) {
          dispatch(
            markSbcSolved({
              setId: sbc.setId,
              sbcId: sbc.id,
              solved: completed,
            }),
          );
        } else {
          dispatch(
            showModal({
              modalType: "login-required",
              freeFeatureLoggedInRequiredHook: "complete-sbc",
            }),
          );
        }
      }}
    />
  );
};

export default GetCardSBC;
